import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { getPublicDeck, listPublicComments, addPublicComment } from "@/lib/api";
import SlideView from "@/components/SlideView";
import { ChevronLeft, ChevronRight, Maximize2, MessageSquare, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function PublicDeck() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [deck, setDeck] = useState(null);
  const [error, setError] = useState("");
  const [current, setCurrent] = useState(0);
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [form, setForm] = useState({ name: "", body: "" });
  const [submitting, setSubmitting] = useState(false);

  const isPresenter = params.get("present") === "1";

  const load = useCallback(async () => {
    try {
      const d = await getPublicDeck(slug);
      setDeck(d);
      try { const c = await listPublicComments(slug); setComments(c); } catch { /* ignore */ }
    } catch { setError("This deck is not available or has not been shared publicly."); }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const next = useCallback(() => setCurrent((c) => (deck ? Math.min(c + 1, deck.slides.length - 1) : c)), [deck]);
  const prev = useCallback(() => setCurrent((c) => Math.max(c - 1, 0)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); prev(); }
      else if (e.key === "Escape") setShowComments(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  const enterFullscreen = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen();
  };

  useEffect(() => { if (isPresenter) enterFullscreen(); }, [isPresenter]);

  const slide = deck?.slides?.[current];
  const slideComments = useMemo(() => comments.filter((c) => c.slide_id === slide?.id), [comments, slide]);

  const submitComment = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.body.trim()) { toast.error("Please add your name and a comment."); return; }
    setSubmitting(true);
    try {
      const c = await addPublicComment(slug, slide.id, form);
      setComments((cs) => [...cs, c]);
      setForm({ name: form.name, body: "" });
      toast.success("Comment added");
    } catch { toast.error("Failed to add comment"); }
    finally { setSubmitting(false); }
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--layor-navy)] text-white text-center px-6">
      <div>
        <div className="font-display text-3xl">Deck not available</div>
        <p className="mt-3 text-white/70">{error}</p>
      </div>
    </div>
  );

  if (!deck) return <div className="min-h-screen flex items-center justify-center bg-black text-white text-sm">Loading…</div>;

  return (
    <div className={`min-h-screen ${isPresenter ? "bg-black" : "bg-[var(--layor-navy)]"} text-white`} data-testid="page-public-deck">
      {!isPresenter && (
        <div className="border-b border-white/10">
          <div className="mx-auto max-w-[1400px] px-4 md:px-6 h-14 flex items-center justify-between">
            <div className="min-w-0">
              <div className="font-display text-base truncate">{deck.title}</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/60">LAYOR Slide Deck · {deck.slides.length} slides</div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowComments((v) => !v)} className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold border border-white/20 hover:bg-white/10" data-testid="toggle-comments">
                <MessageSquare size={12} /> Comments · {comments.length}
              </button>
              <button onClick={enterFullscreen} className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold border border-white/20 hover:bg-white/10" data-testid="fullscreen"><Maximize2 size={12} /> Full-screen</button>
            </div>
          </div>
        </div>
      )}

      <div className={`mx-auto ${isPresenter ? "max-w-none" : "max-w-[1400px] px-4 md:px-6 py-6"}`}>
        <div className={`relative ${isPresenter ? "h-screen" : "aspect-[16/9] rounded-2xl overflow-hidden border border-white/10 bg-white"}`}>
          <SlideView slide={slide} theme={deck.theme} />
          <button onClick={prev} disabled={current === 0} className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/40 text-white flex items-center justify-center disabled:opacity-30" data-testid="prev-slide"><ChevronLeft size={20} /></button>
          <button onClick={next} disabled={current === deck.slides.length - 1} className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/40 text-white flex items-center justify-center disabled:opacity-30" data-testid="next-slide"><ChevronRight size={20} /></button>
          <div className="absolute bottom-3 right-4 text-xs bg-black/50 text-white px-2 py-1 rounded-full">{current + 1} / {deck.slides.length}</div>
        </div>

        {!isPresenter && (
          <>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              {deck.slides.map((s, i) => (
                <button key={s.id} onClick={() => setCurrent(i)} className={`h-2.5 w-6 rounded-full ${i === current ? "bg-[var(--layor-gold)]" : "bg-white/25 hover:bg-white/40"}`} aria-label={`Slide ${i + 1}`} />
              ))}
            </div>

            {showComments && (
              <div className="mt-6 grid md:grid-cols-2 gap-4 bg-white text-[var(--layor-navy)] rounded-2xl p-5" data-testid="comments-panel">
                <div>
                  <div className="eyebrow">Comments on slide {current + 1}</div>
                  {slideComments.length === 0 ? (
                    <p className="mt-3 text-sm text-[var(--layor-muted)]">Be the first to leave a comment.</p>
                  ) : (
                    <ul className="mt-3 space-y-3">
                      {slideComments.map((c) => (
                        <li key={c.id} className="border border-[var(--layor-border)] rounded-xl p-3">
                          <div className="text-xs font-semibold">{c.name} <span className="text-[var(--layor-muted)] font-normal">· {new Date(c.created_at).toLocaleString()}</span></div>
                          <div className="mt-1 text-sm">{c.body}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <form onSubmit={submitComment} className="space-y-3">
                  <div>
                    <Label>Your name</Label>
                    <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="comment-name" />
                  </div>
                  <div>
                    <Label>Your comment</Label>
                    <Textarea required rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="comment-body" />
                  </div>
                  <button type="submit" disabled={submitting} className="btn-primary inline-flex rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="comment-submit">
                    {submitting ? "Sending…" : "Post comment"}
                  </button>
                </form>
              </div>
            )}
          </>
        )}
      </div>

      {isPresenter && (
        <button onClick={() => window.close()} className="fixed top-4 right-4 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><X size={16} /></button>
      )}
    </div>
  );
}
